import React, { useCallback, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { Col, Row, Spin } from 'antd';
import moment from 'moment';
import { generatePath, useHistory, useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/client';

import { cssVariables } from '../../styles/cssVariables';
import { pxToNumber } from '../../utils/cssUtils';
import { numberOr } from '../../common/utils/numberUtils';
import { CurrencyFormat } from '../../components/domainSpecific/currencyElements';
import DynamicTable from '../../components/tables/table';
import { useInvoiceTable } from './invoiceUtils/useInvoiceTable';
import { useDynamicTablePageSize } from './invoiceUtils/useDynamicTablePageSize';
import {
  EMAIL_INVOICE_MUTATION,
  GET_INVOICE_QUERY,
} from '../../app/graphql/invoice';
import { useFlashMessageContext } from '../../components/dialogs/FlashMessageProvider';
import InvoiceContainer from './InvoiceContainer';
import routes from '../../app/routes';
import { useEmailProcessingDialog } from '../useEmailProcessingDialog';
import { useInvoiceStyles } from './invoice.styles';

const INVOICE_SEND_ERROR_MESSAGE = 'Unable to send this Invoice';

const buildInvoiceFilter = searchParams => {
  if (!searchParams) return {};

  const filter = {
    customerId: searchParams.customerId,
  };

  if (searchParams.invoiceNumber) {
    filter.invNumber = searchParams.invoiceNumber;
  }

  if (searchParams.jobNumber) {
    filter.jobNumber = searchParams.jobNumber;
  }

  return filter;
};

export default function InvoiceResultDisplay() {
  const { openDialog, dialog } = useEmailProcessingDialog({
    documentType: 'Invoice',
    paragraph2Id: 'emailProcessingDialog.paragraph2.withAttachment',
  });
  const { errorMessage } = useFlashMessageContext();
  const { styles } = useInvoiceStyles();
  const { containerRef, pageSize } = useDynamicTablePageSize({
    rowHeight: 73,
  });
  const history = useHistory();
  const location = useLocation();
  const { state: searchParams } = location || {};

  const handleJobClick = record => {
    const { jobNumber } = record;
    if (jobNumber) {
      const url = generatePath(routes.shipmentDetail, { jobNumber });
      window.open(url, '_blank');
    }
  };

  const { data, loading, error } = useQuery(GET_INVOICE_QUERY, {
    variables: {
      filter: buildInvoiceFilter(searchParams),
    },
    skip: !searchParams,
    errorPolicy: 'all',
  });
  const hasData = data?.getInvoice?.data?.length > 0;

  const { invoiceJobsColumns, invoiceJobsTableData } = useInvoiceTable(
    data,
    handleJobClick
  );

  const { fromInvoiceList = false } = searchParams;
  const invoice = data?.getInvoice?.data?.[0];
  const invoiceTotal = numberOr(Number(invoice?.invoiceTotal), 0);
  const invoiceDiscount = numberOr(Number(invoice?.invoiceDiscount), 0);
  const currencyCode =
    invoice?.jobs?.find(job => job?.charges?.currency)?.charges?.currency ||
    'USD';

  const summaryItems = [
    {
      key: 'invoiceDate',
      value: moment(invoice?.invoiceDate).format('MMM-DD-YYYY'),
    },
    {
      key: 'numberOfJobs',
      value: invoice?.invoiceNoJobs,
    },
    {
      key: 'invoiceTotal',
      value: (
        <CurrencyFormat value={Number(invoiceTotal)} currency={currencyCode} />
      ),
    },
    ...(invoiceDiscount !== 0
      ? [
          {
            key: 'invoiceDiscount',
            value: (
              <CurrencyFormat
                value={Number(invoiceDiscount)}
                currency={currencyCode}
              />
            ),
          },
        ]
      : []),
  ];
  const summaryColSpan = 24 / summaryItems.length;

  const actionButtons = [
    {
      labelId: 'invoice.email',
      icon: 'paper-plane',
      mutationProps: {
        mutation: EMAIL_INVOICE_MUTATION,
        mutationOptions: {
          onCompleted: emailInvoiceData => {
            const result = emailInvoiceData?.emailInvoice?.result;
            if (result === INVOICE_SEND_ERROR_MESSAGE) {
              errorMessage({ contentId: 'invoice.email.error' });
            } else if (result) {
              openDialog();
            }
          },
          onError: () => {
            errorMessage({ contentId: 'invoice.email.error' });
          },
        },
        getMutationArgs: () => ({
          variables: {
            customer: searchParams.customerId,
            invNumber: searchParams.invoiceNumber,
          },
        }),
      },
    },
  ];

  const handleBack = () => {
    history.goBack();
  };

  const goBackToSearch = useCallback(() => {
    if (fromInvoiceList) {
      history.go(-2);
    } else {
      history.goBack();
    }
  }, [fromInvoiceList, history]);

  useEffect(() => {
    if (error) {
      errorMessage({ contentId: 'error.appError' });
      goBackToSearch();
    } else if (!loading && searchParams && !hasData) {
      errorMessage({ contentId: 'invoice.noData' });
      goBackToSearch();
    }
  }, [error, loading, searchParams, hasData, errorMessage, goBackToSearch]);

  if (loading || !searchParams) {
    return (
      <div className={styles.loading}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !hasData) {
    return (
      <div className={styles.loading}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <>
      <InvoiceContainer
        handleBack={handleBack}
        titleId="invoice.details"
        actionButtons={actionButtons}
      >
        <div className={styles.textContainer}>
          <div className={styles.textWrapper}>
            <div className={styles.number}>
              <span className={styles.numberText}>
                <FormattedMessage id="invoice.invoiceNumber" />
              </span>
              <span className={styles.numberValue}>
                {data?.getInvoice?.data[0]?.invoiceNumber}
              </span>
            </div>
          </div>
        </div>
        <div className={styles.summaryBox}>
          <Row gutter={pxToNumber(cssVariables.spaceNorm2_5)}>
            {summaryItems.map(item => (
              <Col
                xs={24}
                sm={12}
                md={summaryColSpan}
                lg={summaryColSpan}
                key={item.key}
              >
                <div className={styles.summaryItem}>
                  <div className="title">
                    <FormattedMessage id={`invoice.${item.key}`} />
                  </div>
                  <div className="value text-uppercase">{item.value}</div>
                </div>
              </Col>
            ))}
          </Row>
        </div>
        <div className={styles.tableContainer} ref={containerRef}>
          <DynamicTable
            columns={invoiceJobsColumns}
            data={invoiceJobsTableData}
            pagination={{
              pageSize,
            }}
          />
        </div>
      </InvoiceContainer>
      {dialog}
    </>
  );
}
